import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  CurecallInput,
  CurecallButton,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
  CurecallMultiSelect,
  TOption,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { ProjectService, TCreateMedAlertPayload } from 'src/services/project.service'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { getErrorMessage } from 'src/utils/http.util'
import { MedAlertColorRadio } from './med-alert-color-radio.component'
import { EMedAlertUi, TMedAlert } from 'src/types/med-alert'
import { ENABLED_PATIENT_LOCALES, TPatientLocale } from 'src/types/locales'
import { useMemo, useRef, useEffect, useState } from 'react'
import { useProjectMedAlerts } from '../use-project-med-alerts.hook'
import { getSuggestionsForLanguage } from './med-alert-suggestions.helper'
import { useDebouncedCallback } from 'use-debounce'
import { MedAlertSuggestion } from './med-alert-suggestion.component'

type TLanguageOption = TOption<TPatientLocale>

export type TNewMedAlertFormValues = {
  intlLabel: Partial<Record<TPatientLocale, string>>
  ui: EMedAlertUi
}

interface NewMedAlertFormProps {
  onCancel: () => void
  onValidate: (medAlertId: number) => void
}

export function NewMedAlertForm({ onCancel, onValidate }: NewMedAlertFormProps) {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const containerRef = useRef<HTMLDivElement>(null)

  const methods = useForm<TNewMedAlertFormValues>({
    defaultValues: { intlLabel: { fr: '' }, ui: EMedAlertUi.DANGER },
  })
  const {
    formState: { errors },
    control,
    clearErrors,
    setValue,
    watch,
  } = methods

  const { data: medAlerts = [] } = useProjectMedAlerts()

  const [activeSuggestions, setActiveSuggestions] = useState<Partial<Record<TPatientLocale, TMedAlert[]>>>({})

  useEffect(() => {
    // Focus the first input when component mounts
    const firstInput = containerRef.current?.querySelector('input')
    firstInput?.focus()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCreateNew()
    }
  }

  const handleSuggestionClick = (medAlertId: number) => {
    onValidate(medAlertId)
  }

  const updateSuggestions = useDebouncedCallback((language: TPatientLocale, value: string) => {
    const suggestions = getSuggestionsForLanguage(medAlerts, language, value)
    setActiveSuggestions((prev) => ({ ...prev, [language]: suggestions }))
  }, 300)

  const LANGUAGE_OPTIONS: TLanguageOption[] = useMemo(
    () =>
      ENABLED_PATIENT_LOCALES.map((language) => ({
        label: t('common.languages', { language }),
        value: language,
      })),
    [t],
  )

  const intlLabel = watch('intlLabel')

  const selectedLanguageOptions = useMemo(() => {
    const selectedLanguages = Object.keys(intlLabel) as TPatientLocale[]
    return LANGUAGE_OPTIONS.filter((opt) => selectedLanguages.includes(opt.value))
  }, [intlLabel, LANGUAGE_OPTIONS])

  const handleChangeLanguages = (newOptions: TLanguageOption[]) => {
    const newSelectedLanguages = newOptions.map((opt) => opt.value)

    const updatedLabels: Partial<Record<TPatientLocale, string>> = {}
    for (const lang of newSelectedLanguages) {
      updatedLabels[lang] = intlLabel[lang] || ''
    }

    setValue('intlLabel', updatedLabels)
  }

  const getSelectedLanguagesLabel = () => {
    return `${selectedLanguageOptions.length} ${t('pages.configuration.followups.chatbot.node-form.languages-selected')}`
  }

  const handleCreateNew = async () => {
    const isValid = await methods.trigger()
    if (!isValid) return

    const formData = methods.getValues()
    const payload: TCreateMedAlertPayload = {
      intlLabel: formData.intlLabel,
      ui: formData.ui,
      type: 'MEDICOLEGAL',
    }

    try {
      const newMedAlert = await ProjectService.postMedAlert(project.id, payload)
      queryClient.invalidateQueries({ queryKey: ['medAlerts', project.id] })
      onValidate(newMedAlert.id)
    } catch (error) {
      notify(
        {
          title: t('pages.configuration.followups.chatbot.med-alert-modal.error-adding-med-alert'),
          message: getErrorMessage(error),
          appearance: ECurecallNotificationAppearance.ERROR,
        },
        15000,
      )
      return
    }
  }

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown}>
      <FormProvider {...methods}>
        <div className="c-flex c-flex-col c-gap-6">
          <CurecallMultiSelect
            label={t('pages.configuration.followups.chatbot.node-form.node-languages')}
            items={LANGUAGE_OPTIONS}
            selectedItems={selectedLanguageOptions}
            onSelect={handleChangeLanguages}
            selectedElementsTriggerCount={3}
            selectedElementsLabel={getSelectedLanguagesLabel()}
            placeholder={t('pages.configuration.followups.chatbot.node-form.select-languages')}
            isRequired={true}
            wrapperClassName="c-w-full c-relative"
          />

          {selectedLanguageOptions.length > 0 && (
            <div className="c-flex c-flex-col c-gap-4">
              {selectedLanguageOptions.map(({ value: language }) => (
                <Controller
                  key={language}
                  control={control}
                  name={`intlLabel.${language}`}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div>
                      <CurecallInput
                        label={`${t('pages.configuration.followups.chatbot.med-alert-modal.med-alert-label')} (${language.toUpperCase()})`}
                        placeholder={t(
                          'pages.configuration.followups.chatbot.med-alert-modal.med-alert-label',
                        )}
                        tooltipContent={t(
                          'pages.configuration.followups.chatbot.med-alert-modal.med-alert-label-tooltip',
                        )}
                        tooltipPosition="bottom"
                        value={field.value || ''}
                        onChange={(value) => {
                          field.onChange(value)
                          const inputValue = typeof value === 'string' ? value : value.target.value
                          updateSuggestions(language, inputValue)
                          if (errors.intlLabel?.[language] && inputValue) {
                            clearErrors(`intlLabel.${language}`)
                          }
                        }}
                        isRequired={true}
                        errorMessage={errors.intlLabel?.[language] && t('common.form.required')}
                        wrapperClassName="c-w-full"
                      />

                      <MedAlertSuggestion
                        suggestions={activeSuggestions[language] || []}
                        language={language}
                        onSuggestionClick={handleSuggestionClick}
                      />
                    </div>
                  )}
                />
              ))}
            </div>
          )}

          <MedAlertColorRadio />
        </div>

        <div className="c-flex c-justify-center c-gap-10 c-pt-6">
          <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onCancel}>
            {t('common.actions.cancel')}
          </CurecallButton>
          <CurecallButton onClick={handleCreateNew}>
            {t('pages.configuration.followups.chatbot.med-alert-modal.create-and-use')}
          </CurecallButton>
        </div>
      </FormProvider>
    </div>
  )
}
